/* --------------------------------------------------------------------------
   TT-COLLECTION-ASYNC — filtering that fades, and a grid that keeps going.

   Josh, 2026-08-18, on /collections/pangea-table-accessories:
     "Do an infinite scroll instead of paginated pages."
     "When I click say 'Tavern table' the entire page appears to refresh instead
      of a smooth filtering of just the products."

   He was describing exactly what the page did. Every facet is a real link
   carrying a tag path, and every page is a real paginated URL — which is why
   filtering has always worked with JavaScript off, why each state is shareable,
   and why a crawler can walk the whole catalogue. This file keeps every one of
   those properties and stops the navigation.

   HOW. Shopify's Section Rendering API renders any storefront URL down to one
   section: `<the same href>?section_id=<id>`. So the href the link already
   carries is the request — no route table here, no URL arithmetic, nothing that
   can disagree with what Liquid built. Three regions come out of the response:

     [data-tt-controls]  count, units, sort, chips, mobile disclosure — INSTANT
     [data-tt-results]   the grid and the pagination                 — FADES
     [data-tt-rail]      the desktop facet rail                      — INSTANT

   ONLY THE RESULTS ANIMATE. Fading the rail would fade out the checkbox the
   shopper just clicked, which reads as the control breaking rather than as the
   page answering.

   PROGRESSIVE, AND THAT IS NOT A FORMALITY. Every entry point is a real element
   that already worked: the facet links navigate, the sort form submits, the
   numbered pagination pages. If this file does not load, or a fetch fails, the
   page behaves exactly as it did before it existed — every failure path here
   ends in `location.assign`, never in a dead control.

   HISTORY. Filtering and sorting push a URL, so Back steps through the filters a
   shopper actually chose. Infinite scroll pushes NOTHING: appending page 3 does
   not make "page 3" the address of what is on screen — the screen holds pages
   one through three — and writing it would make Back walk pages the shopper
   never navigated to and a refresh drop the first forty-eight products.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* One file, possibly two <script> tags if a page ever carries two collection
     sections. Bind once. */
  if (window.__ttCollectionAsync) return;
  window.__ttCollectionAsync = true;

  var OUT = 'is-swapping';
  var IN = 'is-swapped-in';
  var BUSY = 'is-busy';

  /* Long enough to read as a transition, short enough not to feel like latency.
     Must stay in step with the transition-duration in tt-collection.css; the
     swap is scheduled off this number rather than off `transitionend`, because
     transitionend never fires for a reduced-motion user (there is no
     transition) and the grid would hang at opacity 0 forever. */
  var FADE_MS = 160;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Stale responses must not land. A shopper ticking three facets quickly fires
     three fetches, and they can come back in any order — without this, the
     slowest one wins and the grid disagrees with the rail. */
  var token = 0;

  function root() { return document.querySelector('[data-tt-collection]'); }

  function sectionUrl(href, id) {
    var url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', id);
    return url.toString();
  }

  function fetchSection(href, id) {
    return fetch(sectionUrl(href, id), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  /* Announce the new count to a screen reader. The visible count is inside a
     region that gets replaced, and a live region that is replaced along with the
     content announces nothing — so the message is copied into a node that never
     moves. */
  function announce(scope, doc) {
    var status = scope.querySelector('[data-tt-status]');
    var count = doc.querySelector('.tt-collection__count');
    if (status && count) status.textContent = count.textContent.trim();
  }

  function replace(scope, doc, selector) {
    var to = scope.querySelector(selector);
    var from = doc.querySelector(selector);
    if (!to) return;
    /* A region that exists on this page but not in the response is a REAL
       change, not a miss: the rail disappears when a filter leaves one option,
       and the pagination disappears on the last page. Empty it rather than
       leaving the old markup standing. */
    to.innerHTML = from ? from.innerHTML : '';
  }

  /* ---- filtering and sorting -------------------------------------------- */

  function swap(href, push) {
    var scope = root();
    if (!scope) return;
    var id = scope.dataset.sectionId;
    var results = scope.querySelector('[data-tt-results]');
    var mine = ++token;

    if (results) {
      /* Pin the height for the duration of the fade. Without it the page
         collapses to the height of an empty box and the scroll position jumps,
         which is the opposite of smooth. */
      results.style.minHeight = results.offsetHeight + 'px';
      results.classList.add(OUT);
    }
    scope.classList.add(BUSY);

    var arrived = fetchSection(href, id);
    var faded = new Promise(function (done) { setTimeout(done, reduced ? 0 : FADE_MS); });

    Promise.all([arrived, faded])
      .then(function (both) {
        if (mine !== token) return;              /* a later click already won */
        var doc = both[0];

        replace(scope, doc, '[data-tt-controls]');
        replace(scope, doc, '[data-tt-rail]');
        replace(scope, doc, '[data-tt-results]');
        announce(scope, doc);

        if (results) {
          results.style.minHeight = '';
          results.classList.remove(OUT);
          if (!reduced) {
            /* Force layout between the two classes or the browser coalesces
               them into no change and the entry animation never plays. */
            void results.offsetWidth;
            results.classList.add(IN);
            setTimeout(function () { results.classList.remove(IN); }, 400);
          }
        }
        scope.classList.remove(BUSY);
        armPagination();
        /* Anything else that binds to markup inside a swap region re-runs here.
           tt-color-dots and tt-units are delegated from the document and need
           nothing; the sort control's no-JS submit button is a fresh element
           every swap and does. */
        document.dispatchEvent(new CustomEvent('tt:collection:swapped'));

        if (push) window.history.pushState({ ttCollection: true }, '', href);

        /* Bring the top of the results into view only when it has scrolled off
           the top — filtering from halfway down a long grid otherwise leaves the
           shopper looking at whitespace. Never scroll DOWN to it: yanking the
           page toward a control somebody just used is worse than not moving. */
        var top = scope.getBoundingClientRect().top;
        if (top < 0) window.scrollTo({ top: window.scrollY + top - 24, behavior: reduced ? 'auto' : 'smooth' });
      })
      .catch(function () {
        if (mine !== token) return;
        /* The link still works. Take it. */
        window.location.assign(href);
      });
  }

  /* Same collection, same origin, and not a product link. Anything else is a
     real navigation and is left alone. */
  function isFacetLink(a, scope) {
    if (!a || !a.getAttribute('href')) return false;
    if (a.hasAttribute('download') || a.target === '_blank') return false;
    if (!scope.contains(a)) return false;
    var url;
    try { url = new URL(a.href, window.location.origin); } catch (e) { return false; }
    if (url.origin !== window.location.origin) return false;
    return url.pathname.indexOf('/collections/') === 0;
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    /* Let a modified click do what the shopper asked: open a tab, save the link,
       and so on. These are real URLs and must stay real. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    var scope = root();
    if (!scope) return;

    var more = event.target.closest('[data-tt-next]');
    if (more && scope.contains(more)) {
      event.preventDefault();
      loadMore(more);
      return;
    }

    var a = event.target.closest('a');
    if (!a) return;
    /* Only the facet regions. A product card link is a navigation. */
    var inControls = a.closest('[data-tt-controls]') || a.closest('[data-tt-rail]');
    if (!inControls || !isFacetLink(a, scope)) return;

    event.preventDefault();
    swap(a.href, true);
  });

  /* Sort. tt-collection.js already submits the form on change; catching the
     submit means this file does not have to know that, or how the select is
     named — the form serialises itself. */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.tt-sort');
    var scope = root();
    if (!form || !scope || !scope.contains(form)) return;
    event.preventDefault();
    var url = new URL(form.action, window.location.origin);
    new FormData(form).forEach(function (v, k) { url.searchParams.set(k, v); });
    swap(url.toString(), true);
  });

  window.addEventListener('popstate', function () {
    if (root()) swap(window.location.href, false);
  });

  /* ---- infinite scroll --------------------------------------------------- */

  var observer = null;
  var loading = false;

  function loadMore(button) {
    var scope = root();
    if (!scope || loading) return;
    var href = button.getAttribute('href');
    if (!href) return;

    loading = true;
    button.classList.add(BUSY);
    button.setAttribute('aria-busy', 'true');

    fetchSection(href, scope.dataset.sectionId)
      .then(function (doc) {
        var grid = scope.querySelector('[data-tt-grid]');
        var incoming = doc.querySelector('[data-tt-grid]');
        if (!grid || !incoming) throw new Error('no grid in the response');

        var added = Array.prototype.slice.call(incoming.children);
        added.forEach(function (card, i) {
          if (!reduced) {
            /* Capped, and capped low. A 24-card page at 40ms each would take a
               second to finish arriving, which stops reading as a flourish and
               starts reading as the site being slow. */
            card.style.animationDelay = Math.min(i, 8) * 40 + 'ms';
            card.classList.add('tt-card--in');
          }
          grid.appendChild(card);
        });

        /* The response's pagination is now this page's pagination: it carries
           the NEXT next-URL, or nothing at all on the last page. */
        replace(scope, doc, '[data-tt-pagination]');

        var status = scope.querySelector('[data-tt-status]');
        if (status) status.textContent = added.length + ' more products loaded.';

        loading = false;
        armPagination();
      })
      .catch(function () {
        loading = false;
        button.classList.remove(BUSY);
        button.removeAttribute('aria-busy');
        /* Do not strand the shopper at the bottom of a list that will not grow.
           Hand the page back to the link it came from. */
        window.location.assign(href);
      });
  }

  /* Flip the two controls and watch the button. Called on load and after every
     swap, because both replace the pagination markup wholesale. */
  function armPagination() {
    var scope = root();
    if (!scope) return;

    var pages = scope.querySelector('[data-tt-pages]');
    if (pages) pages.hidden = true;

    var button = scope.querySelector('[data-tt-next]');
    if (!button) return;
    button.hidden = false;
    button.classList.remove(BUSY);
    button.removeAttribute('aria-busy');

    if (observer) observer.disconnect();
    if (!('IntersectionObserver' in window)) return;   /* the button still clicks */

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) loadMore(button);
      });
      /* rootMargin starts the fetch while the button is still below the fold, so
         the next row is usually there before the shopper reaches the end. */
    }, { rootMargin: '600px 0px' });
    observer.observe(button);
  }

  function init() {
    if (root()) armPagination();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* The theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', init);
})();
